// ─────────────────────────────────────────────
//  TimelineTaskCard — the BOARD half of the board→phone directive lane.
//
//  A composer (say something to named people on today's roster) and a ledger
//  (who has seen it). Sibling of BREAKS and MD CALL LIST — same shell, same type
//  scale, same voice. Pure logic lives in src/domain/tasks.js, the subscription
//  in src/hooks/useTasks.js; this file renders and writes.
//
//  The composer is a SENTENCE BUILDER, not a text box: recipients + a verb + an
//  optional counterpart + optional detail, with the finished sentence shown
//  before it is sent. `composeDirective` builds it and the payload calls the
//  same function, so the preview cannot promise one thing and send another. Five
//  rules run through the file, each argued where it is enforced:
//  ⚠️ CONFIRM MEANS "I AM AWARE", NEVER "I DID IT" — see THE SENTENCE.
//  ⚠️ BOTH SIDES OF A HANDOVER ARE TOLD, IN DIFFERENT WORDS — see THE COUNTERPART'S HALF.
//  ⚠️ THIS IS NOT AN ALARM SURFACE — see `pending` and `statusBad` in sx.
//  ⚠️ `recipientIds` ARE AUTH UIDS, NOT STAFF IDS — see THE ONE RESOLUTION.
//  ⚠️ CLEAR HIDES, IT NEVER DELETES — see THE CLEARED VIEW.
// ─────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createTask } from "../../firebase/firestore";
import { uidOf } from "../../domain/channels";
import {
  MAX_TASK_TEXT, buildManualTask, canSend, confirmationLedger,
  outstandingRecipients, pendingUids, requiresConfirm, seenSummary,
  sortTasksNewestFirst,
} from "../../domain/tasks";
// ⚠️ IMPORTED **AND** RE-EXPORTED BELOW. `export { x } from "./y"` alone creates
// NO local binding, so the composer would throw "DIRECTIVE_ACTIONS is not
// defined" at runtime while the build stayed green — the same trap domain/tasks
// records for `formatStampTime`, which threw inside its own module for exactly
// this reason.
import {
  DIRECTIVE_ACTIONS as DIRECTIVE_ACTIONS_LIST,
  actionById,
  allCallPositions,
  composeDirective as composeDirectiveSentences,
  directiveGroups,
  directiveWarnings,
} from "../../domain/directives";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

// Every shape a stamp arrives in — Timestamp, `null` for a frame on the local
// echo of a write, Date offline. Anything else is null, never "Invalid Date".
function stampDate(value) {
  if (!value) return null;
  let d = null;
  if (typeof value.toDate === "function") d = value.toDate();
  else if (value instanceof Date) d = value;
  else if (typeof value === "number") d = new Date(value);
  else if (typeof value.seconds === "number") d = new Date(value.seconds * 1000);
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
}

export function clockLabel(value) {
  const d = stampDate(value);
  if (!d) return null;
  const h24 = d.getHours();
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${String(d.getMinutes()).padStart(2, "0")} ${h24 < 12 ? "AM" : "PM"}`;
}

// ⚠️ +Infinity FOR AN UNSTAMPED ROW — the local echo of the write just made; as 0
// a Clear from before it existed would hide it. (domain/tasks.js keeps a private
// twin for sorting, same reason; not exported.)
const createdMs = (task) => stampDate(task?.createdAt)?.getTime() ?? Number.POSITIVE_INFINITY;

// ── THE CLEARED VIEW ─────────────────────────
//
// ⚠️ CLEAR HIDES, IT DOES NOT DELETE, AND IT REACHES NO PHONE. These documents
// carry the confirmations — the evidence a provider was told — and rules refuse a
// delete on purpose. Clear writes ONE timestamp to THIS browser and hides rows at
// or before it, keyed by uid + facility + day so it cannot follow the operator.
const clearedKey = (uid, facilityId, dateKey) =>
  uid && facilityId && dateKey ? `atc.tasksClearedBefore.${uid}.${facilityId}.${dateKey}` : null;

// localStorage throws in private mode and on a full quota, so both directions
// fail quietly to "nothing cleared" — the view still clears for this session.
function readCleared(key) {
  try {
    const n = key ? Number(window.localStorage.getItem(key)) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}
function writeCleared(key, value) {
  if (!key) return;
  try {
    if (value) window.localStorage.setItem(key, String(value));
    else window.localStorage.removeItem(key);
  } catch {
    /* the stamp lives in component state for this session */
  }
}

// Short role tag for the pickers, so similar names stay distinguishable.
function roleTag(person) {
  const sub = String(person?.subRole || "").toUpperCase();
  return sub || (person?.role === "anesthesiologist" ? "MD" : "");
}

// ── THE SENTENCE ─────────────────────────────
//
// The grammar — every verb, both halves of a handover, and the rules about what
// may cross between them — lives in src/domain/directives.js. It moved there
// when this file passed 500 lines and the verb list stopped being a list, and
// because a sentence a clinician acts on deserves a pure module with its own
// tests rather than a const in a renderer.
//
// ⚠️ RE-EXPORTED, NOT RE-IMPLEMENTED. Callers and tests that reached for these
// through the card keep working, and there is exactly one definition of each.
// Copied logic survives review; copied VOCABULARY drifts, and on a phone the
// manual lane and the automatic one are one system.
export {
  DIRECTIVE_ACTIONS,
  composeDirective,
  composeDirectiveText,
} from "../../domain/directives";

// Static: the page mounts exactly one composer, and Send references them by id.
const PREVIEW_ID = "task-directive-preview";
const PREVIEW_COUNTERPART_ID = "task-directive-preview-counterpart";
// One labelled control, shared by the sketch's top row. `grow` is for the
// recipient field alone: it carries a select AND the Add button.
function Field({ id, label, grow = 1, children }) {
  const style = grow === 1 ? sx.field : { ...sx.field, flexGrow: grow };
  return <div style={style}><label htmlFor={id} style={sx.label}>{label}</label>{children}</div>;
}

// `mark` names someone with no app account. Used in BOTH pickers now: it used to
// be recipient-only, on the argument that marking a person merely NAMED in the
// sentence would imply they were being sent it. That argument died with this
// change — the counterpart IS sent their own half — so the mark is now the plain
// truth on either list, and the operator sees it before choosing rather than in a
// note afterwards.
//
// ⚠️ TWO DIFFERENT ABSENCES, NAMED SEPARATELY. "No app account" is about
// REACHABILITY — there is no phone at the other end. "Left at 15:00" is about
// PRESENCE — there is a phone, it will ring, and the person is at home. They
// used to render identically, so at 16:30 an AIC picking the first familiar
// name could send a takeover to somebody who went home two hours earlier, then
// watch the ledger sit on "pending" while the room stayed uncovered.
//
// ⚠️ MARKED, NEVER HIDDEN — the doctrine this card already follows for
// unreachable people. A name missing from the roster reads as a bug, and a
// departed clinician can genuinely be called back.
const personOption = (p, mark) => {
  const notes = [mark ? "no app account" : "", p.absenceNote || ""].filter(Boolean).join(" · ");
  return (
    <option key={p.id} value={p.id}>
      {`${p.name}${roleTag(p) ? ` · ${roleTag(p)}` : ""}${notes ? ` — ${notes}` : ""}`}
    </option>
  );
};

// ── COMPOSER ─────────────────────────────────
function Composer({
  people, staff, facilityId, dateKey, senderId, senderName,
  rooms = [], campuses = [], originName = "",
}) {
  const [detail, setDetail] = useState("");
  const [action, setAction] = useState("");
  const [counterpartId, setCounterpartId] = useState("");
  const [chosenIds, setChosenIds] = useState([]);
  const [pick, setPick] = useState("");
  const [where, setWhere] = useState("");
  const [when, setWhen] = useState("");
  const [position, setPosition] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { tone: "ok" | "bad", message }

  // ⚠️ THE ONE RESOLUTION THAT MATTERS. A staff id and an auth uid are
  // indistinguishable strings: the doc writes, the ledger shows it sent, no phone
  // rings. Resolved once, here, for reachable and unreachable alike. `uidOf`
  // returns null for pool-only staff and for an unknown id — no phone to reach.
  const chosen = useMemo(
    () =>
      chosenIds
        .map((id) => people.find((p) => p && p.id === id))
        .filter(Boolean)
        .map((person) => ({ ...person, uid: uidOf(staff, person.id) })),
    [chosenIds, people, staff]
  );
  const unreachable = chosen.filter((p) => !p.uid);
  const recipientIds = chosen.filter((p) => p.uid).map((p) => p.uid);

  const actionMeta = actionById(action);
  // ⚠️ THE PLACE CONTROL CHANGES WHAT IT LISTS, NOT JUST WHAT IT IS CALLED.
  // Every verb but one names an OR; `transfer` names a CAMPUS. Offering today's
  // rooms for a cross-campus move would let an operator send somebody to
  // "WOR 5" at a building twenty-five minutes away.
  const whereKind = actionMeta?.whereKind || "room";
  const wherePlaces = whereKind === "campus" ? campuses : rooms;
  const whereLabel = whereKind === "campus" ? "Campus" : "Room";

  // ⚠️ RESET THE PLACE WHEN THE KIND CHANGES. A room left selected from the
  // previous verb would otherwise be sent as a campus, silently.
  useEffect(() => {
    setWhere("");
  }, [whereKind]);

  // ⚠️ NAMED IN ONE DOCUMENT, ADDRESSED IN ANOTHER — never both in one. The
  // counterpart is still absent from the recipient doc's `recipientIds`; they get
  // a SECOND document carrying their own sentence, resolved through the same
  // `uidOf` the recipients go through.
  const counterpartPerson = people.find((p) => p && p.id === counterpartId) || null;
  const counterpartName = counterpartPerson?.name || "";
  const counterpartUid = counterpartPerson ? uidOf(staff, counterpartPerson.id) : null;
  // Two strings for the preview, the counter, the gate and both payloads.
  const { text, counterpartText } = composeDirectiveSentences({
    action,
    counterpartName,
    detail,
    where,
    when,
    position,
    // Where the traveller is coming FROM — this board's own campus. The person
    // at the far end already knows which building they are standing in.
    origin: originName,
    // Only the reachable: the counterpart is told who was actually instructed.
    recipientNames: chosen.filter((p) => p.uid).map((p) => p.name),
  });
  // ⚠️ NEVER TELL SOMEBODY ABOUT THEMSELVES. If the counterpart is also in Send
  // to they already receive the instruction itself; a second, differently-worded
  // message about the same swap on the same phone is two orders, not one.
  const counterpartIsRecipient = Boolean(counterpartUid && recipientIds.includes(counterpartUid));
  const tellsCounterpart = Boolean(counterpartText && counterpartUid && !counterpartIsRecipient);
  // Named in an instruction that is about to go out, with no phone to reach.
  const counterpartUnreachable = Boolean(counterpartText && !counterpartUid);
  const remaining = Math.max(0, MAX_TASK_TEXT - text.length);

  // ── WHAT THE OPERATOR IS TOLD BEFORE SENDING ──
  //
  // ⚠️ WARNINGS, NOT A SILENTLY GREY BUTTON. A disabled control with no reason
  // beside it reads as broken, which is the argument this page already makes for
  // `composeBlockedReason`. A blocking warning explains what to change; a
  // non-blocking one names a risk and leaves the decision with the person who can
  // see the room.
  const warnings = directiveWarnings({
    action,
    where,
    detail,
    position,
    recipientCount: recipientIds.length,
  });
  const blocking = warnings.filter((w) => w.blocking);
  // The domain's gate, against the RESOLVED uids — not the chips on screen.
  // Only-unreachable recipients, or an action addressed to nobody, stay off.
  const ready = canSend({ text, recipientIds }) && blocking.length === 0 && !sending;

  const addPick = () => {
    if (!pick || chosenIds.includes(pick)) return;
    setChosenIds([...chosenIds, pick]);
    setPick("");
  };
  const remove = (id) => setChosenIds(chosenIds.filter((x) => x !== id));

  const submit = async (e) => {
    e.preventDefault();
    // ⚠️ THE ROOM RIDES ON THE DOCUMENT, NOT ONLY IN THE SENTENCE. `roomName` is
    // what the phone's room message box joins on and what the push title reads;
    // this composer wrote null for the whole life of the lane, so every
    // operator-typed directive was invisible in the room it was about. A campus
    // is NOT a room — sending one as `roomName` would join a directive to a room
    // that does not exist.
    const roomName = whereKind === "room" && where ? where : null;
    const payload = buildManualTask({
      facilityId, dateKey, senderId, senderName, recipientIds, text, roomName,
    });
    // Not a duplicate of `ready`: the button can be enabled while
    // facilityId/dateKey/senderId are momentarily absent.
    if (!payload) return;
    // ⚠️ TWO DOCUMENTS, NOT ONE WITH TWO RECIPIENTS. One `recipientIds` array
    // carries one string, and these two people must not read the same string.
    // Separate documents also mean separate confirmations: the board can see
    // that the arriving provider has acknowledged and the leaving one has not.
    // Built HERE from the same `counterpartText` the preview rendered.
    const counterpartPayload = tellsCounterpart
      ? buildManualTask({
          facilityId, dateKey, senderId, senderName,
          recipientIds: [counterpartUid], text: counterpartText, roomName,
        })
      : null;
    setSending(true);
    try {
      try {
        await createTask(payload);
      } catch (err) {
        // ⚠️ VERBATIM. "Something went wrong" hides the one thing that says
        // whether to retry or to fetch someone with rights; a refusal and a
        // dropped connection both arrive here and read nothing alike.
        setStatus({ tone: "bad", message: `Not sent — ${err?.message || "unknown error"}` });
        // The counterpart is NOT attempted. "Arjana Ray is taking over from you"
        // when Arjana never received the order to take over is worse than
        // silence: it is a handover only one side believes in.
        return;
      }
      // ⚠️ A HALF-SENT HANDOVER IS REPORTED AS ONE. The first write landed and
      // cannot be recalled, so this is not a failure — it is a partial send, and
      // the operator has to know WHICH half is missing to close it by voice.
      let counterpartError = null;
      if (tellsCounterpart) {
        // A refused payload cannot happen from here (the recipient's write
        // already proved the facility, the day and the sender), but it is
        // checked rather than assumed: the ONE outcome this feature may never
        // produce is a clean "Sent" while a side was silently skipped.
        if (!counterpartPayload) counterpartError = "their message could not be built.";
        else {
          try {
            await createTask(counterpartPayload);
          } catch (err) {
            counterpartError = err?.message || "unknown error";
          }
        }
      }
      // EVERY control resets: a verb left selected is the next directive
      // inheriting it, silently. It resets on a partial send too — pressing Send
      // again would re-send the half that already arrived, and a duplicate
      // instruction on the arriving provider's phone is its own hazard.
      //
      // ⚠️ THE PLACE AND THE CLOCK RESET TOO, AND THEY MATTER MOST. A room left
      // in the field is the next directive silently inheriting the last one's
      // room, which is worse than an empty field: an empty field is refused, a
      // stale one is sent.
      setDetail("");
      setAction("");
      setCounterpartId("");
      setChosenIds([]);
      setWhere("");
      setWhen("");
      setPosition("");
      const sentTo = `Sent to ${payload.recipientIds.length}`;
      if (counterpartError) {
        setStatus({
          tone: "bad",
          // The verbatim error goes LAST, so no message ending in its own full
          // stop can collide with the sentence that follows it.
          message: `${sentTo}, but ${counterpartName} was NOT told, so tell them another way — ${counterpartError}`,
        });
      } else {
        setStatus({
          tone: "ok",
          message: tellsCounterpart ? `${sentTo}, and to ${counterpartName}.` : `${sentTo}.`,
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <form style={sx.composer} onSubmit={submit}>
      <div style={sx.fieldRow}>
        <Field id="task-directive-recipient" label="Send to" grow={1.8}>
          <div style={sx.pickRow}>
            <select id="task-directive-recipient" value={pick} onChange={(e) => setPick(e.target.value)} style={sx.select}>
              <option value="">Roster…</option>
              {/* LISTED and marked, not hidden: hiding them looks like a bug. */}
              {people.filter((p) => p && p.id && !chosenIds.includes(p.id)).map((p) => personOption(p, !uidOf(staff, p.id)))}
            </select>
            <button type="button" onClick={addPick} disabled={!pick} style={sx.addBtn}>Add</button>
          </div>
        </Field>

        <Field id="task-directive-action" label="Action">
          <select id="task-directive-action" value={action} onChange={(e) => setAction(e.target.value)} style={sx.select}>
            {/* Optional: a detail with no verb is still a valid instruction. */}
            <option value="">Detail only</option>
            {/* ⚠️ GROUPED, NOT A FLAT THIRTEEN. The groups are the order a board
                runner thinks in — cover the room, release the person, stop the
                room, move between campuses — and a flat list this long is one
                nobody reads to the bottom of. Built from the table, never
                retyped. */}
            {directiveGroups().map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.actions.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
              </optgroup>
            ))}
          </select>
        </Field>

        {/* ⚠️ THE ROOM IS A FIELD. Every automatic directive once said "your
            room" and it was reported live; the manual lane had the same defect
            by another route — the verb had a bare form, so the room could only
            live in free text, and free text is optional. Rendered for every
            verb (and for a bare detail) because `roomName` also drives the
            phone's room message box and the push title. */}
        <Field id="task-directive-where" label={whereLabel}>
          <select id="task-directive-where" value={where} onChange={(e) => setWhere(e.target.value)} style={sx.select}>
            <option value="">{whereKind === "campus" ? "No campus" : "No room"}</option>
            {wherePlaces.filter(Boolean).map((r) => {
              const name = typeof r === "string" ? r : r.name || r.label || r.id;
              return name ? <option key={name} value={name}>{name}</option> : null;
            })}
          </select>
        </Field>

        {/* ⚠️ A CLOCK, NEVER A PHRASE. "after this case" is deictic — the
            arriving provider reads it as THEIR case and the departing one as
            THEIRS, so the two walk toward each other at different times. A
            clock time is the same instant for both, which is the only reason a
            time is allowed to cross into the counterpart's half. Relative
            wording belongs in Detail, which does not cross. */}
        <Field id="task-directive-when" label="When">
          <input
            id="task-directive-when" type="time" value={when}
            onChange={(e) => setWhen(e.target.value)} style={sx.select}
          />
        </Field>

        {actionMeta?.counterpart ? (
          <Field id="task-directive-counterpart" label="Provider">
            <select id="task-directive-counterpart" value={counterpartId} onChange={(e) => setCounterpartId(e.target.value)} style={sx.select}>
              <option value="">Nobody</option>
              {people.filter((p) => p && p.id).map((p) => personOption(p, !uidOf(staff, p.id)))}
            </select>
          </Field>
        ) : (
          // ⚠️ SHOWN AND DISABLED FOR A ONE-SIDED VERB, NOT REMOVED. "Come now"
          // and "Call the board" have no other chair. A picker that vanishes
          // reads as a control that broke; one that stays and says what it is
          // reads as a control that does not apply.
          <Field id="task-directive-counterpart" label="Provider">
            <select id="task-directive-counterpart" value="" disabled style={sx.select}>
              <option value="">{action ? "Not used by this action" : "Nobody"}</option>
            </select>
          </Field>
        )}

        {actionMeta?.needsPosition && (
          <Field id="task-directive-position" label="Which call">
            <select id="task-directive-position" value={position} onChange={(e) => setPosition(e.target.value)} style={sx.select}>
              <option value="">Pick the call…</option>
              {allCallPositions().map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        )}
      </div>
      {/* THE INTERPRETATION, ON SCREEN so it can be corrected: two controls list
          the same roster and both now reach somebody, in different words. ONE
          template string — JSX drops the newline before an expression, giving
          "sentence(x)". */}
      <p style={sx.hint}>
        {`Send to = who receives this. Provider = who is named in the sentence${actionMeta && actionMeta.counterpart ? ` (${actionMeta.counterpart})` : ""}, and is optional — they get their own message, written from their side.`}
      </p>
      {chosen.length > 0 && (
        <ul style={sx.chips}>
          {chosen.map((p) => (
            <li key={p.id} style={p.uid ? sx.chip : sx.chipMuted} data-reachable={p.uid ? "yes" : "no"}>
              <span>{p.name}</span>
              {!p.uid && <span style={sx.chipTag}>no app account</span>}
              <button type="button" onClick={() => remove(p.id)} aria-label={`Remove ${p.name}`} style={sx.chipX}>✕</button>
            </li>
          ))}
        </ul>
      )}
      {/* Named, not silently dropped, and NOT an error: never having signed in
          is an ordinary fact, not a fault the operator caused. */}
      {unreachable.length > 0 && (
        <p style={sx.dropNote}>
          {unreachable.length} will not receive this — {unreachable.map((p) => p.name).join(", ")}. They have no app account, so there is no phone to reach.
        </p>
      )}
      {/* ⚠️ THE OTHER SIDE OF THE SWAP, WHEN IT CANNOT BE REACHED. Its own line,
          not folded into the count above, because they were never going to
          receive "this" — they were going to receive their own half, and the
          operator who reads "both were told" and walks away is the whole reason
          this feature exists. */}
      {counterpartUnreachable && (
        <p style={sx.dropNote}>
          {counterpartName} is named in this instruction and will NOT be told — no app account, so there is no phone to reach. Only the people in Send to receive it.
        </p>
      )}
      {/* Quiet, not a warning: they ARE told, once, as a recipient. Said out loud
          only because the second preview line is absent and its absence would
          otherwise look like the counterpart was forgotten. */}
      {counterpartText && counterpartIsRecipient && (
        <p style={sx.hint}>
          {`${counterpartName} is in Send to, so they receive the instruction itself — not a second message about it.`}
        </p>
      )}
      {/* ⚠️ EVERY REFUSAL IS NAMED. A greyed Send with nothing beside it is a
          control that reads as broken. Blocking reasons say what to change; the
          rest name a risk and leave the call to the person who can see the room.
          NOT an alarm surface: ordinary ink on the ordinary note ground, the
          same treatment the unreachable notes already use. */}
      {warnings.length > 0 && (
        <ul style={sx.warnList}>
          {warnings.map((w) => (
            <li key={w.code} style={sx.dropNote} data-blocking={w.blocking ? "yes" : "no"}>
              {w.message}
            </li>
          ))}
        </ul>
      )}
      <label htmlFor="task-directive-text" style={sx.label}>
        {actionMeta?.detailLabel || "Detail"}
      </label>
      {/* A TEXTAREA: at 280 characters a one-line field hides the start of the
          sentence behind its own scroll. Optional — except where a verb makes it
          the reason, and a stop order without a cause gets argued with. */}
      <textarea
        id="task-directive-text" rows={2} value={detail} maxLength={MAX_TASK_TEXT}
        onChange={(e) => setDetail(e.target.value)} style={sx.textarea}
        placeholder={actionMeta?.detailPlaceholder || "e.g. WOR 12, after this case"}
      />
      <div style={sx.counter}>{remaining} characters left</div>
      {/* ⚠️ THE FINISHED SENTENCE, BEFORE IT IS SENT. Three dropdowns do not tell
          an operator what another clinician is about to read, and that clinician
          is not there to ask. Same function as the payload, so this IS the text.
          NOT a live region (every keystroke) — wired to Send with
          aria-describedby, so a screen-reader user hears it as they commit. */}
      <div style={sx.preview}>
        <span style={sx.previewTag}>Sends as</span>
        <span id={PREVIEW_ID} data-testid="directive-preview" style={text ? sx.previewText : sx.previewEmpty}>
          {text || "Pick an action or write a detail — the sentence appears here."}
        </span>
        {/* ⚠️ BOTH SENTENCES, BEFORE EITHER IS SENT. The operator is dispatching
            two clinicians who will act on different words; approving one of the
            two is approving half a handover. Shown ONLY when it will actually
            be written — a sentence in this box that no phone receives is the
            same lie in the other direction. Same function as the payload. */}
        {tellsCounterpart && (
          <span id={PREVIEW_COUNTERPART_ID} style={sx.previewAlso}>
            <span style={sx.previewTag}>{`Also tells ${counterpartName}`}</span>
            <span data-testid="directive-preview-counterpart" style={sx.previewText}>{counterpartText}</span>
          </span>
        )}
      </div>
      <div style={sx.sendRow}>
        {/* Both ids: a screen-reader user commits to both sentences at once. */}
        <button
          type="submit" disabled={!ready} style={ready ? sx.sendBtn : sx.sendBtnOff}
          aria-describedby={tellsCounterpart ? `${PREVIEW_ID} ${PREVIEW_COUNTERPART_ID}` : PREVIEW_ID}
        >
          {sending ? "Sending…" : "Send"}
        </button>
        {/* The ONE live region: announcing each confirmation as it lands would
            talk over the operator mid-sentence. */}
        <span aria-live="polite" style={status?.tone === "bad" ? sx.statusBad : sx.statusOk}>{status?.message || ""}</span>
      </div>
    </form>
  );
}

// ── THE CARD'S OWN WIDTH ─────────────────────
//
// ⚠️ THE CARD IS MEASURED, NOT THE WINDOW, AND THE TWO MOVE OPPOSITE WAYS. This
// card sits in `TimelineSidePanels`' full-width cell (`gridColumn: 1 / -1`),
// and that panel is the page's 3-of-7 side rail above 1280px and the WHOLE page
// below it. So a 1600px window gives this card ~646px and a 1120px window gives
// it ~1061px. A window-width breakpoint gets that exactly backwards — the
// sibling panel records the same trap.
//
// Two layouts, and only `sx.body`'s track list changes between them:
//   split — a fixed composer rail beside a ledger that takes the rest.
//   strip — one column, composer above ledger. What the card has always been.
export const SPLIT_AT = 880;
// Hysteresis, for the same reason the panel uses one: the page's <main> scrolls,
// so a height change can add or remove a scrollbar, which changes the width,
// which could change the layout, which changes the height. A band stops the
// oscillation.
const LAYOUT_BAND = 24;

// Pure, exported, and unit-tested without a layout engine.
//
// ⚠️ AN UNMEASURED CARD IS "split", MATCHING THE PANEL'S OWN `columnsForWidth(0)`.
// jsdom lays nothing out and reports 0 for every box, so every existing test
// renders the split branch. That is deliberate — but it means the strip branch
// is invisible to the suite unless a test stubs ResizeObserver and
// getBoundingClientRect at real widths, which one does.
export function taskCardLayoutFor(width, prev = null) {
  const w = Number(width) || 0;
  if (!w) return "split";
  if (prev === "split") return w < SPLIT_AT - LAYOUT_BAND ? "strip" : "split";
  if (prev === "strip") return w > SPLIT_AT + LAYOUT_BAND ? "split" : "strip";
  return w >= SPLIT_AT ? "split" : "strip";
}

// ⚠️ A CALLBACK REF, NEVER AN EFFECT KEYED ON []. The node this measures can be
// behind a branch that has not rendered yet; an effect keyed on `[]` would run
// once, find nothing, and measure nothing, silently and forever.
//
// ⚠️ AND IT MEASURES A WRAPPER, NEVER THE GRID WHOSE TRACKS IT SETS. Measuring
// the element you are about to re-track is a feedback loop waiting for a style
// edit.
function useCardLayout() {
  const [layout, setLayout] = useState(() => taskCardLayoutFor(0));
  const observerRef = useRef(null);

  const cardRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    const read = () => {
      const w = node.getBoundingClientRect().width;
      setLayout((prev) => taskCardLayoutFor(w, prev));
    };
    read();
    if (typeof ResizeObserver === "undefined") return; // jsdom
    const ro = new ResizeObserver(read);
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);
  return { cardRef, layout };
}

// ── LEDGER ───────────────────────────────────
//
// One receipt per directive. At the card's full width these tile — a lone
// directive fills the region, four sit across a wall display — because the
// ledger is the part somebody watches all day and the composer is the part they
// touch a handful of times a shift.
function LedgerRow({ task, nameByUid }) {
  const rows = confirmationLedger(task, nameByUid);
  const { seen, total, declined } = seenSummary(task);
  const asks = requiresConfirm(task);
  // Complete means every recipient answered YES. It earns the one positive
  // colour on the card and nothing else does — and a refusal must never earn
  // it, or a room somebody has declined reads as settled.
  const complete = asks && total > 0 && seen === total;
  return (
    <li style={complete ? sx.itemDone : sx.item}>
      <div style={sx.itemHead}>
        {/* No stamp = the local echo of the write just made; true for a frame. */}
        <span style={sx.itemTime}>{clockLabel(task.createdAt) || "just now"}</span>
        {task.senderName ? <span style={sx.itemFrom}>{task.senderName}</span> : null}
        {task.roomName ? <span style={sx.itemRoom}>{task.roomName}</span> : null}
        {/* ⚠️ A REFUSAL IS SAID OUT LOUD IN THE SUMMARY. "1 of 3 seen" is true
            and useless when one of the other two has refused: the AIC is
            reading fourteen rooms and needs to know this one needs a new
            person, not to count glyphs to find out. */}
        <span style={sx.summary}>
          {asks ? `${seen} of ${total} seen` : "for information"}
          {declined ? <span style={sx.summaryNo}>{` · ${declined} said no`}</span> : null}
        </span>
      </div>
      {/* THE INSTRUCTION IS THE LARGEST TYPE ON THE CARD. It used to be 12px —
          smaller than the composer's preview and half the size of the selects —
          which put the hierarchy exactly backwards on a card whose entire job is
          to show what was said. */}
      <p style={sx.itemText}>{task.text}</p>
      <ul style={sx.people}>
        {rows.map((r) => (
          <li key={r.uid} style={sx.person} data-seen={r.seen ? "yes" : "no"}>
            {/* ⚠️ THE STATE GLYPH IS THE FIRST COLUMN, AND THAT IS THE WHOLE
                READABILITY FIX. It used to be pinned to the far right with
                `marginLeft: auto`, so at this card's real width a name sat at
                one edge and its status ~900px away with nothing between them.
                A column of ✓ ✓ ○ down the left edge is read as a shape, not
                tracked across a line. */}
            {/* THREE SHAPES, NOT THREE COLOURS: ✓ seen, ✕ refused, ○ waiting.
                The fills sit within a few luminance points of each other, so on
                a wall display and in greyscale the MARK is the entire signal. */}
            <span
              aria-hidden="true"
              style={r.declined ? sx.glyphNo : r.seen ? sx.glyphSeen : sx.glyphWaiting}
            >
              {r.declined ? "✕" : r.seen ? "✓" : "○"}
            </span>
            {/* WRAPS, never truncates. A receipt that cannot say who saw it is
                not a receipt — the sibling BREAKS card made the same call. */}
            <span style={sx.personName}>{r.name}</span>
            {r.declined ? (
              // ⚠️ THE REASON, NOT THE WORD "DECLINED". The board does
              // completely different things about "in an open abdomen for
              // another hour" and "off shift" — and a refusal with no reason is
              // barely better than the silence this whole reply exists to end.
              // Amber, never red: refusing is a legitimate answer, and alarm
              // colour spent here is alarm colour that stops meaning anything.
              <span style={sx.declinedWhy}>{r.reason || "said no"}</span>
            ) : r.seen ? (
              <span style={sx.seen}>{clockLabel(r.at) || "seen"}</span>
            ) : (
              // QUIET BY DECISION. Muted grey, no rail, no red, no blink.
              // "waiting", not "pending": one is what a person is doing, the
              // other is systems vocabulary doing no work at 10px.
              <span style={sx.pending}>{asks ? "waiting" : "sent"}</span>
            )}
          </li>
        ))}
      </ul>
    </li>
  );
}

// ── THE EMPTY DAY ────────────────────────────
//
// ⚠️ FILLED WITH USE, NOT WITH DECORATION. One line of italic grey in a
// thousand-pixel card reads as a card that broke — the same failure the sibling
// panel records for its all-clear state. The verbs are the card's entire
// vocabulary and this is the only place an operator ever meets them, so the
// empty state is where they are taught.
//
// Built FROM the verb table, never retyped: a hand-written list is a second
// definition that goes stale the first time a verb is added.
function EmptyLedger({ cleared }) {
  return (
    <div style={sx.emptyBlock}>
      <p style={sx.emptyHead}>{cleared ? "Nothing new since you cleared." : "Nothing sent today."}</p>
      <p style={sx.emptyBody}>
        {cleared
          ? "Clear hid them from this board only. Providers still have their messages."
          : "A directive you send from here lands on the provider's phone. It comes back stamped when they tap Got it."}
      </p>
      {!cleared && (
        <>
          <p style={sx.emptyEyebrow}>WHAT YOU CAN SEND</p>
          <ul style={sx.emptyVerbs}>
            {DIRECTIVE_ACTIONS_LIST.map((a) => (
              <li key={a.id} style={sx.emptyVerb}>{a.label}</li>
            ))}
          </ul>
          <p style={sx.emptyBody}>
            Only people with an app account can be reached — the roster marks the rest.
          </p>
        </>
      )}
    </div>
  );
}

// How many receipts render before the rest are folded away.
//
// ⚠️ THE CAP CAN NEVER HIDE AN UNANSWERED DIRECTIVE. Every row with somebody
// still to answer is unioned back in regardless of age, so what folds away is
// only fully-answered history. A cap that could bury an open instruction would
// be the same failure as a Clear that buried one, which this card already
// refuses.
export const LEDGER_CAP = 12;

export default function TimelineTaskCard({
  tasks = [], loading = false, people = [], staff = [], facilityId, dateKey,
  senderId, senderName = "", canCompose = false, composeBlockedReason = null,
  // The day's OR rooms and this deployment's campuses, so the place control
  // offers real names instead of asking the operator to type one. Defaulted to
  // empty: a caller that passes neither gets a composer whose place-requiring
  // verbs refuse to send, which is the honest outcome — not one that invents a
  // room.
  rooms = [], campuses = [], originName = "",
}) {
  // Sorted again though useTasks sorts: O(n), and a raw array from a future
  // caller cannot then render oldest-first — the order IS the meaning here.
  const ordered = useMemo(() => sortTasksNewestFirst(tasks), [tasks]);
  const { cardRef, layout } = useCardLayout();
  const [showAll, setShowAll] = useState(false);

  // uid → display name. An unnameable uid still renders, as "Unknown".
  const nameByUid = useMemo(() => {
    const map = {};
    [...(staff || []), ...(people || [])].forEach((p) => {
      if (!p) return;
      const uid = p.uid || uidOf(staff, p.id);
      if (uid && p.name) map[uid] = p.name;
    });
    return map;
  }, [staff, people]);

  const storeKey = clearedKey(senderId, facilityId, dateKey);
  const [clearedBefore, setClearedBefore] = useState(0);
  // Re-read on every KEY change: another campus or day must not inherit a clear.
  useEffect(() => setClearedBefore(readCleared(storeKey)), [storeKey]);

  const visible = useMemo(() => ordered.filter((t) => createdMs(t) > clearedBefore), [ordered, clearedBefore]);
  const hidden = ordered.length - visible.length;
  // Cleared rows still unanswered: clearing is a view preference, not an answer.
  const hiddenUnseen = useMemo(
    () => outstandingRecipients(ordered.filter((t) => createdMs(t) <= clearedBefore)).length,
    [ordered, clearedBefore]
  );
  const outstanding = useMemo(() => outstandingRecipients(visible), [visible]);

  // The newest N, plus every row somebody still owes an answer to. Order is
  // preserved from `visible`, so the union cannot shuffle the list.
  const shown = useMemo(() => {
    if (showAll || visible.length <= LEDGER_CAP) return visible;
    const keep = new Set(visible.slice(0, LEDGER_CAP));
    visible.forEach((t) => {
      if (pendingUids(t).length > 0) keep.add(t);
    });
    return visible.filter((t) => keep.has(t));
  }, [visible, showAll]);
  const folded = visible.length - shown.length;

  const clear = () => {
    // The newest STAMPED row: an unstamped one is a write still in flight, and
    // storing its +Infinity would hide every directive for the rest of the day.
    const stamps = visible.map(createdMs).filter(Number.isFinite);
    if (!stamps.length) return;
    const stamp = Math.max(...stamps);
    writeCleared(storeKey, stamp);
    setClearedBefore(stamp);
  };
  const restore = () => {
    writeCleared(storeKey, 0);
    setClearedBefore(0);
  };

  // ⚠️ WITH NO COMPOSER THERE IS NO RAIL TO PUT BESIDE THE LEDGER. A read-only
  // viewer, and a wall display, get the full width for the thing they are
  // actually here to read.
  const split = layout === "split" && canCompose;

  return (
    <section style={sx.card} data-testid="task-directives-card" data-layout={layout} ref={cardRef}>
      <header style={sx.cardHead}>
        <h2 style={sx.cardTitle}>
          TASK DIRECTIVES
          {/* Counts what is ON SCREEN: a badge larger than the rows beneath it
              is a number nobody can reconcile. */}
          <span style={sx.cardCount}>{visible.length}</span>
        </h2>
        <span style={sx.cardNote}>a receipt that they saw it</span>
      </header>

      <div style={split ? sx.bodySplit : sx.bodyStrip}>
        {/* ── THE RAIL: compose, then who still owes an answer ── */}
        <div style={sx.rail}>
          {canCompose ? (
            <div style={sx.region}>
              <h3 style={sx.regionTitle}>COMPOSE</h3>
              <Composer
                {...{ people, staff, facilityId, dateKey, senderId, senderName }}
                {...{ rooms, campuses, originName }}
              />
            </div>
          ) : (
            composeBlockedReason && <p style={sx.blocked}>{composeBlockedReason}</p>
          )}

          {/* ⚠️ ONE THOUGHT IN ONE PLACE. The count of who has not answered, the
              names, the Clear button and the tally of what Clear hid used to be
              split across the full height of the card — the summary above the
              list, the control below it. They belong together, beside the hands
              that act on them. */}
          {!loading && ordered.length > 0 && (
            <div style={sx.region}>
              <h3 style={sx.regionTitle}>WAITING ON</h3>
              {/* THE GAP, QUIETLY: a count and names, no rail, no tint, no alert. */}
              {outstanding.length > 0 ? (
                <p style={sx.outstanding}>
                  Not seen yet ({outstanding.length}): {outstanding.map((uid) => nameByUid[uid] || "Unknown").join(", ")}
                </p>
              ) : (
                // ⚠️ WORDED SO IT CANNOT BE FALSE. With rows hidden by Clear
                // this says only what it can see; the cleared line underneath
                // supplies the rest. Claiming "everyone has seen everything"
                // while holding unanswered rows out of view would be the lie
                // this card exists to prevent.
                <p style={sx.outstanding}>
                  {hiddenUnseen > 0 ? "Nobody waiting in the list below." : "Everyone has seen today's directives."}
                </p>
              )}
              {/* ⚠️ SAID PLAINLY, NEXT TO THE BUTTON: an operator who believes
                  Clear retracted an instruction stops expecting the answer to
                  it. Disabled once everything is cleared — a control that
                  vanishes reads as a control that broke. */}
              <div style={sx.clearRow}>
                <button type="button" onClick={clear} disabled={visible.length === 0}
                  style={visible.length === 0 ? sx.clearBtnOff : sx.clearBtn}>
                  Clear
                </button>
                <span style={sx.clearNote}>Clears your board view only — providers keep their messages.</span>
              </div>
              {/* Nothing was destroyed, so the way back is always on screen — and
                  it names the cleared rows still unanswered, so tidying cannot
                  bury one. */}
              {hidden > 0 && (
                <p style={sx.clearedLine}>
                  {`${hidden} cleared${hiddenUnseen > 0 ? ` · ${hiddenUnseen} still not seen` : ""} · `}
                  <button type="button" onClick={restore} style={sx.linkBtn}>Show</button>
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── THE LEDGER: what was sent, and who has answered ── */}
        <div style={sx.ledger}>
          <div style={sx.regionHead}>
            <h3 style={sx.regionTitle}>SENT TODAY</h3>
            {visible.length > 0 && <span style={sx.regionNote}>newest first</span>}
          </div>
          {loading ? (
            <div style={sx.emptyBlock}>
              <p style={sx.emptyHead}>Loading today&apos;s directives…</p>
              <p style={sx.emptyBody}>Today&apos;s directives and their receipts are on the way.</p>
            </div>
          ) : visible.length === 0 ? (
            <EmptyLedger cleared={hidden > 0} />
          ) : (
            <>
              <ul style={sx.list}>
                {shown.map((t) => (
                  <LedgerRow key={t.id} task={t} nameByUid={nameByUid} />
                ))}
              </ul>
              {folded > 0 && (
                <p style={sx.moreLine}>
                  {`${folded} older, all seen · `}
                  <button
                    type="button" onClick={() => setShowAll(true)} style={sx.linkBtn}
                    aria-label={`Show all ${visible.length} directives`}
                  >
                    Show all
                  </button>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// `sx` not `s`, to avoid shadowing confusion next to the siblings.
const sx = {
  card: { background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.lg, padding: "11px 13px 13px", minWidth: 0, boxShadow: tokens.shadow.sm },
  cardHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 9, paddingBottom: 7, borderBottom: `1px solid ${color.border.default}` },
  cardTitle: { margin: 0, fontSize: 11, fontWeight: font.weight.bold, letterSpacing: ".9px", color: color.text.secondary, display: "flex", alignItems: "center", gap: 7 },
  cardCount: { fontSize: 10, fontWeight: font.weight.bold, background: color.surface.raised, color: color.text.secondary, borderRadius: radius.full, padding: "1px 7px", letterSpacing: 0 },
  cardNote: { fontSize: 10.5, color: color.text.muted, whiteSpace: "nowrap" },
  // -- STRUCTURE --------------------------------
  //
  // The ONLY thing the breakpoint changes is this track list. Everything below
  // is flex-wrap and needs no measurement.
  //
  // WARNING: `alignItems: "start"` IS SAFE HERE AND WOULD NOT BE ONE CELL OVER.
  // The card is the sole item in its panel row (`gridColumn: 1 / -1`), so the
  // cell height IS this card's content height and there is no bare page for a
  // short column to expose -- the hole the sibling panel was rebuilt to close.
  // If this card is ever placed BESIDE another, give `sx.card` height:100% plus
  // a column flex and `sx.body*` flex:1, or the shorter of the two reopens it.
  bodySplit: { display: "grid", gridTemplateColumns: "380px minmax(0, 1fr)", columnGap: 24, rowGap: 16, alignItems: "start", minWidth: 0 },
  bodyStrip: { display: "grid", gridTemplateColumns: "minmax(0, 1fr)", rowGap: 16, minWidth: 0 },
  rail: { display: "flex", flexDirection: "column", gap: 16, minWidth: 0 },
  ledger: { display: "flex", flexDirection: "column", gap: 8, minWidth: 0 },
  region: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  regionHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, minWidth: 0 },
  // One notch under the card's h2, and the same shape -- the three cards in this
  // panel have to keep reading as one family.
  regionTitle: { margin: 0, fontSize: 11, fontWeight: font.weight.bold, letterSpacing: ".8px", color: color.text.secondary },
  regionNote: { fontSize: 10.5, color: color.text.muted, whiteSpace: "nowrap" },
  composer: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 0 },
  label: { fontSize: 10.5, fontWeight: font.weight.bold, letterSpacing: ".7px", color: color.text.muted, textTransform: "uppercase" },
  // 16px on every FOCUSABLE field: iOS zooms into smaller ones, and this page
  // stacks onto a phone below 1280px. Buttons take no caret.
  textarea: { width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: 16, lineHeight: 1.35, color: color.text.primary, background: color.surface.base, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "6px 8px", resize: "vertical" },
  counter: { fontSize: 10.5, color: color.text.muted, textAlign: "right" },
  fieldRow: { display: "flex", flexWrap: "wrap", gap: 7, alignItems: "flex-end", minWidth: 0 },
  field: { display: "flex", flexDirection: "column", gap: 3, flex: "1 1 150px", minWidth: 0 },
  hint: { margin: "5px 0 2px", fontSize: 10, lineHeight: 1.4, color: color.text.muted },
  pickRow: { display: "flex", gap: 6, alignItems: "center", minWidth: 0 },
  select: { width: "100%", flex: 1, minWidth: 0, boxSizing: "border-box", fontFamily: "inherit", fontSize: 16, color: color.text.primary, background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "5px 7px" },
  addBtn: { fontSize: 11, fontWeight: font.weight.bold, fontFamily: "inherit", background: color.surface.raised, color: color.text.secondary, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "6px 11px", cursor: "pointer", minHeight: 30 },
  chips: { listStyle: "none", margin: "4px 0 0", padding: 0, display: "flex", flexWrap: "wrap", gap: 4 },
  chip: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: font.weight.bold, color: color.text.primary, background: color.surface.base, border: `1px solid ${color.border.default}`, borderRadius: radius.full, padding: "2px 4px 2px 9px" },
  chipMuted: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: color.text.muted, background: color.surface.card, border: `1px dashed ${color.border.default}`, borderRadius: radius.full, padding: "2px 4px 2px 9px" },
  chipTag: { fontSize: 10.5, letterSpacing: ".3px", textTransform: "uppercase" },
  // WCAG 2.2 SC 2.5.8: 24x24 minimum. It measured 21x23, and it is transparent,
  // so growing the box costs no ink — only hit area.
  chipX: { fontFamily: "inherit", fontSize: 11, lineHeight: 1, color: color.text.secondary, background: "transparent", border: "none", borderRadius: radius.full, padding: "5px 8px", cursor: "pointer", minHeight: 24, minWidth: 24, display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" },
  // A list, so a screen reader announces how many refusals there are rather
  // than reading three paragraphs that look unrelated. Rows reuse `dropNote`:
  // this is not an alarm surface, and a refusal to send is not an emergency.
  warnList: { listStyle: "none", margin: "4px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 4 },
  dropNote: { margin: "4px 0 0", fontSize: 10, lineHeight: 1.4, color: color.text.secondary, background: color.surface.base, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "5px 7px" },
  preview: { display: "flex", flexDirection: "column", gap: 2, marginTop: 6, background: color.surface.base, border: `1px solid ${color.accent.primary}`, borderRadius: radius.sm, padding: "6px 8px", minWidth: 0 },
  previewTag: { fontSize: 10.5, fontWeight: font.weight.bold, letterSpacing: ".7px", color: color.text.muted, textTransform: "uppercase" },
  previewText: { fontSize: 13, lineHeight: 1.35, fontWeight: font.weight.semibold, color: color.text.primary },
  // The second half, divided by a rule rather than a colour: it is equally real,
  // not a footnote and not a warning.
  previewAlso: { display: "flex", flexDirection: "column", gap: 2, marginTop: 5, paddingTop: 5, borderTop: `1px solid ${color.border.default}`, minWidth: 0 },
  previewEmpty: { fontSize: 12, lineHeight: 1.35, color: color.text.muted, fontStyle: "italic" },
  sendRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 6, minWidth: 0 },
  sendBtn: { fontSize: 12, fontWeight: font.weight.bold, fontFamily: "inherit", background: color.accent.primary, color: "#fff", border: "1px solid transparent", borderRadius: radius.sm, padding: "7px 15px", cursor: "pointer", minHeight: 32 },
  sendBtnOff: { fontSize: 12, fontWeight: font.weight.bold, fontFamily: "inherit", background: color.surface.raised, color: color.text.muted, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "7px 15px", cursor: "not-allowed", minHeight: 32 },
  statusOk: { fontSize: 10.5, color: color.text.secondary, minWidth: 0 },
  // AA ink on white (#991b1b, 7.60:1); accent.error as TEXT is 4.41:1 and FAILS
  // (theme/tokens.js). The one red here, and it reports a failed WRITE only.
  statusBad: { fontSize: 10.5, color: color.surface.errorInk, minWidth: 0 },
  blocked: { margin: "0 0 10px", fontSize: 10.5, lineHeight: 1.45, color: color.text.secondary, background: color.surface.base, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "7px 9px" },
  // WARNING: THE RECEIPTS TILE, THEY DO NOT STACK. Five directives ran 450px
  // DOWN a card that was 1000px ACROSS. `1 1 300px` lets one fill the region,
  // two sit side by side, four across a wall display. `stretch` keeps a row of
  // them the same height so the wrap grid stays tidy.
  list: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 8, minWidth: 0 },
  item: { flex: "1 1 300px", background: color.surface.base, border: `1px solid ${color.border.default}`, borderLeft: `3px solid ${color.border.default}`, borderRadius: radius.sm, padding: "7px 9px", minWidth: 0 },
  // The one positive colour on the card, marking the one honest positive fact:
  // every recipient answered. A BORDER, not a fill -- 1.4.11 asks 3:1 of a
  // non-text indicator and this clears it, while a green wash would make a
  // routine receipt the loudest object on the board.
  itemDone: { flex: "1 1 300px", background: color.surface.base, border: `1px solid ${color.border.default}`, borderLeft: `3px solid ${color.status.active.border}`, borderRadius: radius.sm, padding: "7px 9px", minWidth: 0 },
  itemRoom: { fontSize: 11, fontWeight: font.weight.bold, color: color.text.secondary, whiteSpace: "nowrap" },
  itemHead: { display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap", minWidth: 0 },
  itemTime: { fontSize: 11, fontWeight: font.weight.bold, color: color.text.secondary, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", whiteSpace: "nowrap" },
  itemFrom: { fontSize: 11, color: color.text.muted, minWidth: 0 },
  summary: { fontSize: 11, fontWeight: font.weight.semibold, color: color.text.secondary, marginLeft: "auto", whiteSpace: "nowrap" },
  // WARNING: THE LARGEST NON-HEADING TYPE ON THE CARD, and it was the smallest.
  // This is the string the card exists to display; it was 12px regular, under
  // the composer's own 13px preview and well under the 16px selects.
  itemText: { margin: "4px 0 6px", fontSize: font.size.body, fontWeight: font.weight.semibold, lineHeight: 1.35, color: color.text.primary, overflowWrap: "anywhere" },
  people: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 2 },
  // WARNING: A GRID, NOT A FLEX ROW WITH `marginLeft: auto`. The old row pinned
  // the status to the far edge, so at this card's real width a name sat at one
  // end and "seen 3:42 PM" ~900px away with nothing to carry the eye across.
  // Glyph | name | stamp, and the glyph column is what makes it scannable from
  // across a room.
  person: { display: "grid", gridTemplateColumns: "16px minmax(0, 1fr) auto", gap: 8, alignItems: "baseline", minWidth: 0 },
  // WRAPS. A receipt that cannot say who saw it is not a receipt, and on this
  // card the name is the payload -- the sibling BREAKS card made the same call.
  personName: { fontSize: font.size.label, color: color.text.primary, minWidth: 0, overflowWrap: "anywhere" },
  // Tokenised. This was a raw #096e53 that already existed as
  // `status.active.text` -- one value with two definitions is how they drift.
  seen: { fontSize: 11, color: color.status.active.text, whiteSpace: "nowrap" },
  glyphSeen: { fontSize: 11, lineHeight: 1.2, color: color.status.active.text, textAlign: "center" },
  glyphWaiting: { fontSize: 11, lineHeight: 1.2, color: color.text.muted, textAlign: "center" },
  pending: { fontSize: 11, color: color.text.muted, whiteSpace: "nowrap" },
  // Amber, deliberately NOT the alarm red. A refusal is an ordinary, correct
  // answer — a directive can land during an induction — and alarm colour is
  // finite: spend it here and staff learn to ignore the colour that means
  // somebody is dying.
  glyphNo: { fontSize: 11, lineHeight: 1.2, color: color.status.closing.text, textAlign: "center" },
  declinedWhy: { fontSize: 11, color: color.status.closing.text, fontWeight: font.weight.bold, minWidth: 0 },
  summaryNo: { color: color.status.closing.text, fontWeight: font.weight.bold },
  clearRow: { display: "flex", alignItems: "center", gap: 7, margin: 0, minWidth: 0, flexWrap: "wrap" },
  clearBtn: { fontSize: 11, fontWeight: font.weight.bold, fontFamily: "inherit", background: color.surface.raised, color: color.text.secondary, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "5px 11px", cursor: "pointer", minHeight: 28, flexShrink: 0 },
  clearBtnOff: { fontSize: 11, fontWeight: font.weight.bold, fontFamily: "inherit", background: color.surface.card, color: color.text.muted, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "5px 11px", cursor: "not-allowed", minHeight: 28, flexShrink: 0 },
  clearNote: { fontSize: 11, lineHeight: 1.35, color: color.text.muted, minWidth: 0 },
  clearedLine: { margin: 0, fontSize: 11, lineHeight: 1.4, color: color.text.muted },
  // A BUTTON that looks like a link: it changes this view, it goes nowhere.
  // WCAG 2.2 SC 2.5.8: 24x24 minimum. It measured 18px tall — the smallest
  // target on the card. Transparent, so this is hit area, not weight.
  linkBtn: { fontFamily: "inherit", fontSize: 11, fontWeight: font.weight.bold, color: color.accent.primary, background: "transparent", border: "none", padding: "3px 2px", textDecoration: "underline", cursor: "pointer", minHeight: 24, minWidth: 24, display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" },
  outstanding: { margin: 0, fontSize: 11, lineHeight: 1.45, color: color.text.secondary },
  // WARNING: A FRAMED BLOCK THAT FILLS THE REGION, NOT ONE ITALIC LINE. Italic
  // grey in a thousand-pixel card reads as a card that broke. Neutral ground,
  // never the green all-clear one: nothing here is an assertion of safety.
  emptyBlock: { background: color.surface.base, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "11px 13px", display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  emptyHead: { margin: 0, fontSize: 12.5, fontWeight: font.weight.semibold, color: color.text.secondary },
  emptyBody: { margin: 0, fontSize: 11, lineHeight: 1.45, color: color.text.muted },
  emptyEyebrow: { margin: "4px 0 0", fontSize: 11, fontWeight: font.weight.bold, letterSpacing: ".8px", color: color.text.muted },
  emptyVerbs: { listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "2px 12px", minWidth: 0 },
  emptyVerb: { fontSize: 11, color: color.text.secondary, minWidth: 0 },
  moreLine: { margin: "2px 0 0", fontSize: 11, lineHeight: 1.4, color: color.text.muted },
};
